/**
 * Turn a DICOM-encapsulated MPEG instance into a browser-playable MP4.
 *
 * Browsers cannot play the raw bitstream carried in a DICOM video object's Pixel
 * Data (it is an MPEG-2 / H.264 / HEVC elementary stream, not an MP4 container),
 * so we:
 *   1. fetch the `.dcm` over the existing CDN-signed URL (same bytes the
 *      Cornerstone path would fetch — no new backend endpoint required),
 *   2. parse it with dicom-parser and concatenate the encapsulated Pixel Data
 *      fragments into the elementary stream,
 *   3. remux/transcode it to fragmented MP4 with ffmpeg.wasm.
 *
 * H.264 streams are stream-copied (fast, no re-encode); MPEG-2 is transcoded to
 * H.264 for cross-browser playback. The result is returned as an object URL the
 * caller assigns to a <video> element.
 */
import { vlog } from '@/lib/video/debug';
import { withFfmpeg } from '@/lib/video/ffmpeg';
import dicomParser from 'dicom-parser';
import { getVideoCodec } from './videoTransferSyntax';
export class DicomVideoError extends Error {
    cause;
    constructor(message, cause) {
        super(message);
        this.cause = cause;
        this.name = 'DicomVideoError';
    }
}
/** Fetch the DICOM object and extract the encapsulated video elementary stream. */
async function extractEncapsulatedStream(storageLocation, signal) {
    const res = await fetch(storageLocation, { signal });
    if (!res.ok) {
        throw new DicomVideoError(`Failed to fetch DICOM object (${res.status})`);
    }
    const buffer = await res.arrayBuffer();
    const byteArray = new Uint8Array(buffer);
    let dataSet;
    try {
        dataSet = dicomParser.parseDicom(byteArray);
    }
    catch (e) {
        throw new DicomVideoError('Failed to parse DICOM object', e);
    }
    const transferSyntaxUid = dataSet.string('x00020010');
    const codec = getVideoCodec(transferSyntaxUid);
    if (!codec) {
        throw new DicomVideoError(`Unsupported transfer syntax for video: ${transferSyntaxUid ?? '?'}`);
    }
    const pixelElement = dataSet.elements.x7fe00010;
    if (!pixelElement) {
        throw new DicomVideoError('DICOM object has no Pixel Data');
    }
    if (!pixelElement.encapsulatedPixelData || !pixelElement.fragments?.length) {
        throw new DicomVideoError('Pixel Data is not encapsulated; cannot extract video stream');
    }
    // Concatenate all fragments (skipping the Basic Offset Table) to reconstruct
    // the elementary stream. For most MPEG objects this is a single fragment.
    const fragments = pixelElement.fragments;
    const totalLength = fragments.reduce((sum, f) => sum + f.length, 0);
    const data = new Uint8Array(totalLength);
    let offset = 0;
    for (const fragment of fragments) {
        data.set(byteArray.subarray(fragment.position, fragment.position + fragment.length), offset);
        offset += fragment.length;
    }
    // [DIAG] confirm extraction succeeded and what we're handing ffmpeg.
    vlog('[dicomVideo] extracted stream:', {
        transferSyntaxUid,
        codec,
        bytes: data.length,
        fragments: fragments.length,
        head: Array.from(data.slice(0, 12)),
    });
    return { data, codec };
}
function inputFileName(codec) {
    switch (codec) {
        case 'h264':
            return 'in.h264';
        case 'hevc':
            return 'in.hevc';
        case 'mpeg2':
            return 'in.mpg';
    }
}
function ffmpegArgs(codec, input) {
    // The DICOM Pixel Data fragment may be a raw elementary stream OR a full MP4 /
    // MPEG-TS container (PS3.5 8.2.7 permits both). ffmpeg probes the actual format
    // from content, so we avoid input options (e.g. -framerate) that are only valid
    // for raw demuxers and would error on a container.
    switch (codec) {
        case 'h264':
            // Stream-copy H.264 into an MP4 container (no re-encode).
            return ['-i', input, '-c', 'copy', '-movflags', '+faststart', 'out.mp4'];
        case 'hevc':
            return ['-i', input, '-c', 'copy', '-tag:v', 'hvc1', '-movflags', '+faststart', 'out.mp4'];
        case 'mpeg2':
            // Browsers don't decode MPEG-2; transcode to H.264.
            return [
                '-i',
                input,
                '-c:v',
                'libx264',
                '-preset',
                'veryfast',
                '-pix_fmt',
                'yuv420p',
                '-movflags',
                '+faststart',
                'out.mp4',
            ];
    }
}
/**
 * Produce a playable MP4 blob for a DICOM video instance. Throws
 * DicomVideoError on any failure so callers can show a fallback.
 */
export async function loadDicomVideo(storageLocation, signal) {
    const { data, codec } = await extractEncapsulatedStream(storageLocation, signal);
    const input = inputFileName(codec);
    return withFfmpeg(async (ffmpeg) => {
        try {
            await ffmpeg.writeFile(input, data);
            const args = ffmpegArgs(codec, input);
            vlog('[dicomVideo] running ffmpeg', args);
            const exitCode = await ffmpeg.exec(args);
            vlog('[dicomVideo] ffmpeg exit code', exitCode);
            if (exitCode !== 0) {
                throw new DicomVideoError(`ffmpeg exited with code ${exitCode} while remuxing ${codec}`);
            }
            const out = await ffmpeg.readFile('out.mp4');
            const bytes = typeof out === 'string' ? new TextEncoder().encode(out) : out;
            const blob = new Blob([bytes], { type: 'video/mp4' });
            return { blob, codec };
        }
        catch (e) {
            if (e instanceof DicomVideoError)
                throw e;
            throw new DicomVideoError(`Failed to transcode ${codec} video`, e);
        }
        finally {
            void ffmpeg.deleteFile(input).catch(() => { });
            void ffmpeg.deleteFile('out.mp4').catch(() => { });
        }
    });
}
// Poster frames are cached per source so the series strip and the video
// viewport share one extraction (and we never decode the same clip twice).
const posterCache = new Map();
/**
 * Extract a single still (the first frame) from a DICOM video instance as a
 * JPEG object URL, for use as a series thumbnail / <video> poster. Cached by
 * storage location. Throws DicomVideoError on failure.
 */
export function getDicomVideoPoster(storageLocation) {
    let cached = posterCache.get(storageLocation);
    if (!cached) {
        cached = generatePoster(storageLocation);
        posterCache.set(storageLocation, cached);
        // Don't cache failures — allow a later retry.
        cached.catch(() => posterCache.delete(storageLocation));
    }
    return cached;
}
async function generatePoster(storageLocation) {
    const { data, codec } = await extractEncapsulatedStream(storageLocation);
    const input = inputFileName(codec);
    return withFfmpeg(async (ffmpeg) => {
        try {
            await ffmpeg.writeFile(input, data);
            const exitCode = await ffmpeg.exec([
                '-i',
                input,
                '-frames:v',
                '1',
                '-vf',
                "scale='min(320,iw)':-2",
                '-q:v',
                '5',
                'poster.jpg',
            ]);
            if (exitCode !== 0) {
                throw new DicomVideoError(`ffmpeg exited with code ${exitCode} while extracting poster`);
            }
            const out = await ffmpeg.readFile('poster.jpg');
            const bytes = typeof out === 'string' ? new TextEncoder().encode(out) : out;
            // react-doctor-disable-next-line react-doctor/no-create-object-url-without-revoke -- posterCache shares this small JPEG URL across callers for the page's lifetime on purpose (see use-video-poster)
            return URL.createObjectURL(new Blob([bytes], { type: 'image/jpeg' }));
        }
        catch (e) {
            if (e instanceof DicomVideoError)
                throw e;
            throw new DicomVideoError(`Failed to extract poster from ${codec} video`, e);
        }
        finally {
            void ffmpeg.deleteFile(input).catch(() => { });
            void ffmpeg.deleteFile('poster.jpg').catch(() => { });
        }
    });
}
